import { ClerkProvider } from "@clerk/clerk-react";
import { arSA, enUS } from "@clerk/localizations";
import { MantineProvider, ColorSchemeScript } from "@mantine/core";

import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { PostHogProvider } from "posthog-js/react";
import { I18nextProvider } from "react-i18next";
import { Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router";
import Spinner from "./components/shared/Spinner";
import { env } from "./env";
import i18n from "./i18n";
import { theme } from "./theme";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";

const queryClient = new QueryClient();

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>tkana | Platform</title>
        <link
          href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;700&family=Tajawal:wght@400;500;700&display=swap"
          rel="stylesheet"
        />
        <ColorSchemeScript defaultColorScheme="light" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function Root() {
  const hasPostHogKey = !!env.VITE_PUBLIC_POSTHOG_KEY;

  const appContent = (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <ClerkProvider
          signInUrl="/login"
          signUpUrl="/register"
          signInForceRedirectUrl="/"
          signUpForceRedirectUrl="/"
          publishableKey={env.VITE_CLERK_PUBLISHABLE_KEY}
          localization={i18n.language === "ar" ? arSA : enUS}
        >
          <MantineProvider theme={theme} defaultColorScheme="light">
            <Notifications position="top-right" />
            <ModalsProvider>
              <Outlet />
            </ModalsProvider>
          </MantineProvider>
        </ClerkProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );

  // Only wrap with PostHogProvider if we have a valid key
  if (hasPostHogKey && env.VITE_PUBLIC_POSTHOG_KEY) {
    return (
      <PostHogProvider
        apiKey={env.VITE_PUBLIC_POSTHOG_KEY}
        options={{
          api_host: "https://track.tkana.sa",
          defaults: "2025-05-24",
          capture_exceptions: true,
          debug: false,
        }}
      >
        {appContent}
      </PostHogProvider>
    );
  }

  // Return app without PostHog if no key is provided
  return appContent;
}

export function HydrateFallback() {
  return <Spinner />;
}
